import React from 'react';

import Icon from '../../../components/AppIcon';

const ContentRequirements = ({ data, updateData, errors }) => {
  const contentTypeOptions = [
    { value: 'video', label: 'Video', icon: 'Video', description: 'Full-length video content' },
    { value: 'story', label: 'Story', icon: 'Smartphone', description: 'Short-form stories' },
    { value: 'post', label: 'Post', icon: 'Image', description: 'Static image posts' },
    { value: 'reel', label: 'Reel', icon: 'Film', description: 'Short vertical videos' },
    { value: 'live', label: 'Live Stream', icon: 'Radio', description: 'Live streaming content' }
  ];

  const toggleContentType = (value) => {
    const currentTypes = data?.contentTypes || [];
    if (currentTypes?.includes(value)) {
      updateData('contentTypes', currentTypes?.filter(type => type !== value));
    } else {
      updateData('contentTypes', [...currentTypes, value]);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Content Requirements</h2>
        <p className="text-muted-foreground">Specify what type of content you need and creative guidelines</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-foreground mb-3 block">
            Content Deliverable Types <span className="text-destructive">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {contentTypeOptions?.map((type) => {
              const isSelected = data?.contentTypes?.includes(type?.value);
              return (
                <button
                  key={type?.value}
                  type="button"
                  onClick={() => toggleContentType(type?.value)}
                  className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all text-left ${
                    isSelected
                      ? 'border-primary bg-primary/10' :'border-border bg-background hover:border-primary/50'
                  }`}
                >
                  <Icon name={type?.icon} size={24} className={isSelected ? 'text-primary' : 'text-muted-foreground'} />
                  <div className="flex-1">
                    <p className={`font-medium ${
                      isSelected ? 'text-primary' : 'text-foreground'
                    }`}>
                      {type?.label}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{type?.description}</p>
                  </div>
                  {isSelected && (
                    <Icon name="Check" size={20} className="text-primary flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
          {errors?.contentTypes && (
            <p className="text-sm text-destructive mt-2">{errors?.contentTypes}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Creative Guidelines
          </label>
          <textarea
            className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Describe your brand voice, visual style, dos and don'ts, key messages to include..."
            value={data?.creativeGuidelines || ''}
            onChange={(e) => updateData('creativeGuidelines', e?.target?.value)}
          />
          <p className="text-sm text-muted-foreground mt-1">
            Help influencers understand your brand and create content that aligns with your vision
          </p>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Brand Assets (Optional)
          </label>
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer">
            <Icon name="Upload" size={32} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">Upload brand assets</p>
            <p className="text-xs text-muted-foreground mb-3">
              Logos, music snippets, visual templates (Max 10MB per file)
            </p>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Icon name="FolderOpen" size={16} />
              Choose Files
            </button>
          </div>
          {data?.brandAssets?.length > 0 && (
            <div className="mt-3 space-y-2">
              {data?.brandAssets?.map((asset, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <Icon name="File" size={20} className="text-muted-foreground" />
                  <span className="text-sm text-foreground flex-1">{asset?.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const newAssets = data?.brandAssets?.filter((_, i) => i !== index);
                      updateData('brandAssets', newAssets);
                    }}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
          <Icon name="Lightbulb" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-medium mb-1">Content Best Practices</p>
            <ul className="list-disc list-inside space-y-1 text-amber-800">
              <li>Give influencers creative freedom while maintaining brand consistency</li>
              <li>Provide clear examples of content you like</li>
              <li>Specify any mandatory elements (hashtags, mentions, music clips)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentRequirements;
